/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw, Ban, Cpu, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { upsertAiUsageTenant, getAiUsageEvents, formatCompact, formatBytes } from '@/lib/aiUsage';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export const usageTone = (pct) => (pct >= 100 ? 'text-destructive' : pct >= 80 ? 'text-warning' : 'text-success');

/** Barra di consumo con etichetta used/limit e percentuale. */
export const QuotaBar = ({ used, limit, pct, testId }) => (
  <div className="min-w-[160px]" data-testid={testId}>
    <div className="flex items-center justify-between text-[11px] mb-1">
      <span className="tabular-nums">{formatCompact(used)} / {formatCompact(limit)}</span>
      <span className={cn('font-semibold tabular-nums', usageTone(pct))}>{pct}%</span>
    </div>
    <Progress value={Math.min(100, pct)} className={cn('h-2', pct >= 100 && '[&>div]:bg-destructive', pct >= 80 && pct < 100 && '[&>div]:bg-warning')} />
  </div>
);

/** KPI compatto nell'header della pagina. */
export const KpiTile = ({ label, value, hint, Icon, testId }) => (
  <div className="rounded-xl border border-border/70 bg-card p-3 flex items-start gap-3" data-testid={testId}>
    {Icon && <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon className="h-4 w-4" /></div>}
    <div className="min-w-0">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-lg leading-tight tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground truncate">{hint}</div>}
    </div>
  </div>
);

/** Dialog: crea/modifica piano di una clinica (tier → limite predefinito, limite custom, reset). */
export const PlanDialog = ({ open, onOpenChange, tenant, tiers, onSaved }) => {
  const isNew = !tenant;
  const [clinicId, setClinicId] = useState('');
  const [tier, setTier] = useState('Entry');
  const [limit, setLimit] = useState(1000);
  const [reset, setReset] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClinicId(tenant?.clinic_id || '');
    setTier(tenant?.plan_tier || 'Entry');
    setLimit(tenant?.max_monthly_requests ?? (tiers?.Entry ?? 1000));
    setReset(false);
    setNotes(tenant?.notes || '');
  }, [open, tenant, tiers]);

  const pickTier = (t) => { setTier(t); if (tiers?.[t] != null) setLimit(tiers[t]); };

  const save = async (e) => {
    e?.preventDefault?.();
    const cid = clinicId.trim();
    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/.test(cid)) { toast.error('ID clinica non valido: solo lettere, numeri, punto, trattino, underscore (max 64)'); return; }
    setSaving(true);
    try {
      const saved = await upsertAiUsageTenant(cid, { plan_tier: tier, max_monthly_requests: Number(limit) || 0, reset_counter: reset, notes: notes.trim() || undefined });
      toast.success(`Piano ${saved.plan_tier} salvato per ${saved.clinic_id}: ${formatCompact(saved.max_monthly_requests)} richieste/mese`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Salvataggio non riuscito');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-md" data-testid="ai-usage-plan-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nuova clinica' : `Piano · ${tenant.clinic_id}`}</DialogTitle>
          <DialogDescription>I limiti vivono nel database: la modifica è immediata, senza redeploy.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">ID clinica (header x-clinic-id)</Label>
            <Input value={clinicId} onChange={(e) => setClinicId(e.target.value)} disabled={!isNew || saving} placeholder="clinica-sanmarco" className="h-11 rounded-xl font-mono text-xs" data-testid="ai-usage-plan-clinic-id" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Tier</Label>
              <Select value={tier} onValueChange={pickTier} disabled={saving}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="ai-usage-plan-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(tiers || {}).map(([k, v]) => <SelectItem key={k} value={k}>{k} · {formatCompact(v)}/mese</SelectItem>)}
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Richieste AI / mese</Label>
              <Input type="number" min={0} value={limit} onChange={(e) => setLimit(e.target.value)} disabled={saving} className="h-11 rounded-xl tabular-nums" data-testid="ai-usage-plan-limit" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Note interne (opzionale)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} placeholder="Contratto 2026, referente…" className="h-11 rounded-xl" data-testid="ai-usage-plan-notes" />
          </div>
          {!isNew && (
            <div className="flex items-center justify-between rounded-xl border border-border/70 p-3">
              <div className="text-sm"><b>Azzera contatore ora</b><p className="text-[11px] text-muted-foreground">Riparte un nuovo ciclo di 30 giorni (es. upgrade a metà mese).</p></div>
              <Switch checked={reset} onCheckedChange={setReset} disabled={saving} data-testid="ai-usage-plan-reset" />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-xl">Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-xl" data-testid="ai-usage-plan-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva piano
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const statusTone = (e) => (e.blocked ? 'border-destructive/50 text-destructive' : e.status >= 400 ? 'border-warning/50 text-warning' : 'border-success/50 text-success');

/** Dialog: ultimi eventi AI di una clinica. */
export const EventsDialog = ({ open, onOpenChange, tenant, month }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open || !tenant) return;
    setItems(null);
    getAiUsageEvents(tenant.clinic_id, { month, limit: 60 }).then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [open, tenant, month]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ai-usage-events-dialog">
        <DialogHeader>
          <DialogTitle>Telemetria · {tenant?.clinic_id}</DialogTitle>
          <DialogDescription>Ultime chiamate AI del mese {month}. Token conteggiati con tiktoken (o stima caratteri/4 se non disponibile).</DialogDescription>
        </DialogHeader>
        {items === null ? <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          : items.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ai-usage-events-empty">Nessuna chiamata AI registrata in questo mese.</div>
            : (
              <Table>
                <TableHeader><TableRow><TableHead>Quando</TableHead><TableHead>Route</TableHead><TableHead>Esito</TableHead><TableHead className="text-right">Token in/out</TableHead><TableHead className="text-right">Traffico</TableHead><TableHead className="text-right">Latenza</TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((e) => (
                    <TableRow key={e.id} data-testid="ai-usage-event-row">
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(e.ts)}</TableCell>
                      <TableCell className="text-xs font-mono"><span className="text-muted-foreground">{e.method}</span> {e.path.replace('/api', '')}<div className="text-[10px] text-muted-foreground">{e.family}</div></TableCell>
                      <TableCell><Badge variant="outline" className={cn('text-[10px] gap-1', statusTone(e))}>{e.blocked ? <Ban className="h-3 w-3" /> : e.status >= 400 ? <RotateCcw className="h-3 w-3" /> : <Cpu className="h-3 w-3" />}{e.blocked ? '402 bloccata' : e.status >= 400 ? `${e.status} rimborsata` : `${e.status} conteggiata`}</Badge></TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{e.tokens_in} / {e.tokens_out}<div className="text-[10px] text-muted-foreground">{e.token_method}</div></TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{formatBytes(e.request_bytes)} ↑<br />{formatBytes(e.response_bytes)} ↓</TableCell>
                      <TableCell className="text-right text-xs tabular-nums"><Clock className="h-3 w-3 inline mr-1 opacity-60" />{e.latency_ms} ms</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </DialogContent>
    </Dialog>
  );
};
